/**
 * Credit-related Query Hooks
 * Handles credit balance, history, and analytics
 */

import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api.js';
import { queryKeys } from '../../lib/queryKeys.js';

/**
 * Get current credit balance
 * Stale time: 30 seconds (default)
 */
export function useCreditBalance(options = {}) {
    return useQuery({
        queryKey: queryKeys.credits,
        queryFn: async () => {
            const response = await api.getCreditBalance();
            return response;
        },
        staleTime: 30 * 1000, // 30 seconds
        ...options,
    });
}

/**
 * Get credit transaction history
 * @param {Object} historyOptions - { limit: number }
 */
export function useCreditHistory(historyOptions = { limit: 20 }, queryOptions = {}) {
    return useQuery({
        queryKey: queryKeys.creditHistory(historyOptions),
        queryFn: async () => {
            const response = await api.getCreditHistory(historyOptions);
            return response;
        },
        staleTime: 60 * 1000, // 1 minute
        ...queryOptions,
    });
}

/**
 * Get credit analytics (usage trends, predictions)
 * @param {Object} analyticsOptions - { timeframe, includeComparison, includePredictions, includeFraudCheck }
 */
export function useCreditAnalytics(analyticsOptions = {}, queryOptions = {}) {
    const defaultOptions = {
        timeframe: 'monthly',
        includeComparison: true,
        includePredictions: true,
        includeFraudCheck: false,
    };

    const mergedOptions = { ...defaultOptions, ...analyticsOptions };

    return useQuery({
        queryKey: queryKeys.creditAnalytics(mergedOptions),
        queryFn: async () => {
            const response = await api.getCreditAnalytics(mergedOptions);
            return response;
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
        ...queryOptions,
    });
}

/**
 * Get plan limits and current usage
 */
export function usePlanLimits(options = {}) {
    return useQuery({
        queryKey: queryKeys.planLimits,
        queryFn: async () => {
            const response = await fetch('/api/user/plan-limits', {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error('Failed to fetch plan limits');
            }
            return response.json();
        },
        staleTime: 60 * 1000, // 1 minute
        ...options,
    });
}

export default {
    useCreditBalance,
    useCreditHistory,
    useCreditAnalytics,
    usePlanLimits,
};
